<template lang="pug">
    .fs-gallery__wrapper(
      @click="emitClose"
    )
        .fs-gallery__close
            SvgAsset(:content="closeIconContent")
        .fs-gallery(
          ref="wrapper"
        )
            .fs-gallery__slides(ref="gallery")
                .fs-gallery__slide(v-for="(url, index) in imageUrls")
                    img(:src="url" @click.stop="NOOP" :srcset="srcset[index]")
        .fs-gallery__control.fs-gallery__next(@click.stop="goToNextSlide"  v-show="isNextVisible")
        .fs-gallery__control.fs-gallery__prev(@click.stop="goToPrevSlide" v-show="isPrevVisible")
</template>

<script lang="ts">
  import type { PropOptions } from 'vue';
  import Siema from 'siema';
  import SvgAsset from '@devhacker/shared/components/SvgAsset.vue';
  import closeIconContent from '~/assets/images/close.svg?raw';

  import PageScroll from '~/libs/PageScroll';
  import { NOOP } from '~/utils';

  type Data = {
    siema: Siema | null;
    targetElement: Element | null;
  };

  export default defineNuxtComponent({
    name: 'FullScreenGallery',
    components: {
      SvgAsset,
    },
    props: {
      imageUrls: {
        type: Array,
        required: true,
      } as PropOptions<string[]>,
      currentSlide: {
        type: Number,
        required: true,
      } as PropOptions<number>,
      srcset: {
        type: [String, Array],
        default: '',
      } as PropOptions<string | string[]>,
    },
    data(): Data {
      return {
        siema: null,
        targetElement: null,
      };
    },
    computed: {
      closeIconContent() {
        return closeIconContent;
      },
      isNextVisible(): boolean {
        return this.siema !== null && this.siema.currentSlide < this.imageUrls.length - 1;
      },
      isPrevVisible(): boolean {
        return this.siema !== null && this.siema.currentSlide > 0;
      },
    },
    watch: {
      currentSlide(newSlide: number): void {
        this.siema && this.siema.goTo(newSlide);
      },
    },
    mounted() {
      PageScroll.disable();

      const gallery = this.$refs.gallery as HTMLElement;
      this.siema = new Siema({
        selector: gallery,
        draggable: 'ontouchstart' in document.documentElement,
        startIndex: this.currentSlide,
        onChange: () => {
          this.siema && this.$emit('go-to', this.siema.currentSlide);
        },
      });

      window.addEventListener('keyup', this.handleKeyup, false);
    },
    beforeUnmount() {
      PageScroll.enable(this.targetElement);
      window.removeEventListener('keyup', this.handleKeyup, false);
    },
    methods: {
      NOOP,

      goToNextSlide(): void {
        this.siema && this.siema.next();
      },
      goToPrevSlide(): void {
        this.siema && this.siema.prev();
      },
      emitClose(): void {
        this.$emit('close');
      },
      handleKeyup(e: KeyboardEvent): void {
        switch (e.code) {
          case 'Escape':
            this.emitClose();
            break;
          case 'ArrowRight':
            this.goToNextSlide();
            break;
          case 'ArrowLeft':
            this.goToPrevSlide();
            break;
          default:
            break;
        }
      },
    },
  });
</script>

<style lang="scss" scoped>
  .fs-gallery {
    position: relative;
    background: transparent;
    width: 100vw;
    max-width: 1280px;
    height: 100%;

    @include big-tablet {
      width: calc(100vw - 160px);
    }

    &__close {
      position: absolute;
      top: 12px;
      right: 12px;
      cursor: pointer;
      z-index: 1;

      :deep(.svg-asset) {
        width: 32px;
        height: 32px;
        path {
          fill: #acb6cd;
        }
        @include hover {
          path {
            fill: #e1eafd;
          }
        }
      }

      @include big-tablet {
        top: 22px;
        right: 22px;
      }
    }

    &__wrapper {
      position: fixed;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(0, 0, 0, 0.75);
      z-index: 20;
    }

    &__slides {
      height: 100%;

      :deep() {
        & > div,
        & > div > div {
          height: 100%;
        }
      }
    }

    &__slide {
      text-align: center;
      display: flex;
      justify-content: center;
      align-items: center;
      // fix for https://youtrack.lifehacker.ru/issue/LH-572
      transform: translate3d(0, 0, 0);
      height: 100%;

      img {
        max-height: 100%;
        max-width: 100%;
        width: auto;
      }
    }

    &__control {
      display: none;
      @include big-tablet {
        display: block;
      }

      box-shadow: 0 3px 10px rgba(0, 0, 0, 0.25);
      cursor: pointer;

      position: absolute;
      transform: translateY(-50%);
      top: 50%;
      width: 32px;
      height: 32px;
      border-radius: 20px;
      background: white;
      &:after {
        width: 8px;
        height: 8px;
        content: '';
        display: block;
        border-radius: 1px;
        position: absolute;
        transform: rotate(-135deg);
      }
    }

    &__prev {
      left: 24px;

      &:after {
        border-top: 2px solid rgb(117, 117, 117);
        border-right: 2px solid rgb(117, 117, 117);
        top: 11px;
        left: 13px;
      }
    }

    &__next {
      right: 24px;

      &:after {
        border-bottom: 2px solid rgb(117, 117, 117);
        border-left: 2px solid rgb(117, 117, 117);
        top: 11px;
        left: 10px;
      }
    }
  }
</style>
